// Pixar Singing Ad's draft as the form keeps it (spec G14-pixar-singing-ad, PRs 19b and 19c): the
// server holds the member's draft (`.../templates/pixar-singing-ad/draft`), so it survives the
// panel; the form reads it, keeps its edits there, and asks the server's pack reader, lyrics
// director, song takes, shot director and stills (live's `usePixarSingingDraft`, which ran them
// from the browser).
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { ApiError } from "../../../api/errors";
import {
  getGetPixarSingingAdDraftQueryKey,
  useAddPixarSingingAdTake,
  useApprovePixarSingingAdLyrics,
  useApprovePixarSingingAdSong,
  useBackToPixarSingingAdLyrics,
  useBackToPixarSingingAdTakes,
  useGetPixarSingingAdDraft,
  useRegeneratePixarSingingAdStill,
  useResetPixarSingingAdDraft,
  useWritePixarSingingAdDraft,
  useWritePixarSingingAdLyrics,
  writePixarSingingAdDraft,
} from "../../../api/generated/endpoints/generation/generation";
import type {
  OutputRef,
  PixarSingingDraftStill,
  PixarSingingStillName,
  PixarLyrics,
  PixarSingingAdDraft,
} from "../../../api/generated/model";
import {
  PixarSingingAdDraftWrite,
  PixarSingingAdInputs,
  PixarSingingAdLyricsApproval,
  PixarSingingAdLyricsAsk,
  type PixarSingingAdDraftWriteOutput,
  type PixarSingingAdInputsOutput,
} from "../../../api/generated/zod";
import { soonest, urlRefreshIn, urlsDue } from "../../../api/signed-urls";

/** The form as it stands: its picks and its lyrics. */
export interface DraftForm {
  inputs: PixarSingingAdInputsOutput;
  lyrics: PixarLyrics | null;
}

/** The cast's stills in live's order (`W/ids.ts`), and what each is drawn from: the sheet from
 * the photo or from words, each shot from the one before and the sheet; the end card from the
 * packshot and the lyrics alone. */
export const STILL_ORDER: readonly PixarSingingStillName[] = ["char_sheet", "shot_1", "shot_2", "shot_3", "end_card"];
const STILL_SOURCES: Readonly<Partial<Record<PixarSingingStillName, readonly PixarSingingStillName[]>>> = {
  shot_1: ["char_sheet"],
  shot_2: ["shot_1", "char_sheet"],
  shot_3: ["shot_2", "char_sheet"],
};
/** Live's card labels (`STILL_LABELS`). */
export const STILL_LABELS: Readonly<Record<PixarSingingStillName, string>> = {
  char_sheet: "Character",
  shot_1: "Shot 1",
  shot_2: "Shot 2",
  shot_3: "Shot 3",
  end_card: "End card",
};

/** The draft refetched while it works: every 2 s while a director, a take or a still works, and
 * a moment after (a step's end moves the draft a moment after its job ends). */
const POLL_MS = 2_000;
const SETTLE_MS = 6_000;
/** The form's edits are kept on the server once typing pauses this long. */
const SAVE_WAIT_MS = 800;

/** The video's settings, editable until "Generate final video" as live's footer keeps them
 * (Subtitles and the resolution): the form's own until the video sends them. */
export interface VideoSettings {
  subtitles: boolean;
  caption_position: "top" | "center" | "bottom";
  caption_style_id: string;
  resolution: "720p" | "1080p";
}

/** "Generate final video"'s body (live's hand-off): the picked take's song and timing map, the
 * shot spec, the stills the clips start on by reference, and the form's captions and resolution;
 * null until every still of the plan is drawn. */
export function videoBody(draft: PixarSingingAdDraft, settings: VideoSettings) {
  if (draft.stage !== "cast" || draft.spec === null || draft.take_timing === null) return null;
  const take = draft.takes.find((each) => each.number === draft.picked_take);
  if (take?.output == null) return null;
  const drawn: Partial<Record<PixarSingingStillName, OutputRef>> = {};
  for (const still of draft.stills) {
    if (still.status === "done" && still.output != null) drawn[still.name] = still.output;
  }
  if (draft.stills.length === 0 || draft.stills.some((still) => drawn[still.name] === undefined)) return null;
  const { char_sheet: _sheet, ...clipStills } = drawn;
  return {
    type: "video.template" as const,
    inputs: {
      template: "pixar-singing-ad" as const,
      take: { song: take.output, timing: draft.take_timing },
      spec: draft.spec,
      stills: clipStills,
      ...settings,
    },
  };
}

/** A still under way (live's `isStillWaiting`): drawing, or pending on stills that are all done or
 * still coming; a shot also waits for the shot director. */
export function stillWaiting(name: PixarSingingStillName, draft: PixarSingingAdDraft): boolean {
  const stills = draft.stills;
  const status = stills.find((each) => each.name === name)?.status;
  if (status === "working") return true;
  if (status !== "pending") return false;
  if (name !== "end_card" && draft.spec === null) return draft.shot_director === "working";
  const sources = (STILL_SOURCES[name] ?? []).filter((source) => stills.some((each) => each.name === source));
  return sources.every(
    (source) => stills.find((each) => each.name === source)?.status === "done" || stillWaiting(source, draft),
  );
}

/** Where the draft's work stands, as live's progress line names it. */
export interface DraftWork {
  reading: boolean;
  writing: boolean;
  takes: boolean;
  planning: boolean;
  drawing: boolean;
}

export function workOf(draft: PixarSingingAdDraft, asked: string | null): DraftWork {
  const answering = asked === draft.updated_at && draft.stage === "lyrics" && draft.problem === null;
  return {
    reading: draft.pack_director === "working",
    writing: draft.lyrics_director === "working" || (answering && draft.pack_director !== "working"),
    takes: draft.takes.some((take) => take.status === "working"),
    planning: draft.shot_director === "working",
    drawing: draft.stills.some((still) => stillWaiting(still.name, draft)),
  };
}

function working(work: DraftWork): boolean {
  return work.reading || work.writing || work.takes || work.planning || work.drawing;
}

/** The form a draft holds, its picks with the contract's defaults. */
export function formOf(draft: PixarSingingAdDraft): DraftForm {
  const inputs = PixarSingingAdInputs.safeParse(draft.inputs);
  return {
    inputs: inputs.success ? inputs.data : PixarSingingAdInputs.parse({}),
    lyrics: draft.lyrics,
  };
}

/** The draft's stills of its plan, in live's order. */
export function planStills(draft: PixarSingingAdDraft): PixarSingingDraftStill[] {
  return STILL_ORDER.flatMap((name) => draft.stills.filter((each) => each.name === name));
}

/** A refusal because the draft is locked (`TEMPLATE_DRAFT_LOCKED`). */
function locked(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

/**
 * The member's draft and what the form does with it, as Street Interview Ad's (`street-draft.ts`):
 * the form shows the draft as read; edits at the lyrics, nothing working, are kept with PUT a
 * moment after typing pauses; each ask answers with the draft, shown from then on.
 */
export function usePixarDraft(teamId: string, brandId: string) {
  const queryClient = useQueryClient();
  const key = getGetPixarSingingAdDraftQueryKey(teamId, brandId);
  const [asked, setAsked] = useState<string | null>(null);
  const [local, setLocal] = useState<DraftForm | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const lastBusy = useRef(0);
  const read = useGetPixarSingingAdDraft(teamId, brandId, {
    query: {
      refetchInterval: (query) => {
        const data = query.state.data;
        const now = Date.now();
        if (data !== undefined && working(workOf(data, asked))) lastBusy.current = now;
        const poll = now - lastBusy.current < SETTLE_MS ? POLL_MS : false;
        return soonest(poll, urlRefreshIn(data?.urls_expire_at, now));
      },
      refetchOnWindowFocus: (query) => (urlsDue(query.state.data?.urls_expire_at) ? "always" : true),
    },
  });
  const draft = read.data;
  const refetch = () => void queryClient.invalidateQueries({ queryKey: key });
  const took = (answer: PixarSingingAdDraft) => {
    queryClient.setQueryData(key, answer);
    lastBusy.current = Date.now();
    setLocal(null);
  };
  const refused = (error: unknown) => {
    setFailure(error);
    if (locked(error)) refetch();
  };
  const asks = { mutation: { onSuccess: took, onError: refused } };
  const lyrics = useWritePixarSingingAdLyrics({
    mutation: {
      onSuccess: (answer) => {
        took(answer);
        setAsked(answer.updated_at);
      },
      onError: refused,
    },
  });
  const lyricsApproval = useApprovePixarSingingAdLyrics(asks);
  const take = useAddPixarSingingAdTake(asks);
  const toLyrics = useBackToPixarSingingAdLyrics(asks);
  const songApproval = useApprovePixarSingingAdSong(asks);
  const toTakes = useBackToPixarSingingAdTakes(asks);
  const regeneration = useRegeneratePixarSingingAdStill(asks);
  const reset = useResetPixarSingingAdDraft(asks);
  const asking = [lyrics, lyricsApproval, take, toLyrics, songApproval, toTakes, regeneration, reset].some(
    (mutation) => mutation.isPending,
  );
  const work: DraftWork =
    draft === undefined
      ? { reading: false, writing: false, takes: false, planning: false, drawing: false }
      : workOf(draft, asked);
  const busy = asking || working(work);
  const server = draft === undefined ? null : formOf(draft);
  const form = draft?.stage === "lyrics" && local !== null ? local : server;
  const editable = draft?.stage === "lyrics" && !busy;

  const save = useWritePixarSingingAdDraft({
    mutation: {
      onSuccess: () => setFailure(null),
      onError: (error) => {
        if (locked(error)) {
          setLocal(null);
          refetch();
        } else {
          setFailure(error);
        }
      },
    },
  });
  const { mutate: write } = save;
  const unsaved = useRef<{ teamId: string; brandId: string; data: PixarSingingAdDraftWriteOutput } | null>(null);
  useEffect(
    () => () => {
      const pending = unsaved.current;
      if (pending !== null) {
        writePixarSingingAdDraft(pending.teamId, pending.brandId, pending.data).catch(() => undefined);
      }
    },
    [],
  );
  useEffect(() => {
    if (local === null || !editable) return undefined;
    const body = PixarSingingAdDraftWrite.safeParse({ inputs: local.inputs, lyrics: local.lyrics });
    if (!body.success) return undefined;
    const data = body.data;
    unsaved.current = { teamId, brandId, data };
    const timer = setTimeout(() => {
      unsaved.current = null;
      write({ teamId, brandId, data });
    }, SAVE_WAIT_MS);
    return () => {
      clearTimeout(timer);
      unsaved.current = null;
    };
  }, [local, editable, teamId, brandId, write]);

  const ask = () => setFailure(null);
  return {
    read,
    draft,
    form,
    work,
    busy,
    editable,
    failure,
    approving: lyricsApproval.isPending || songApproval.isPending,
    resetting: reset.isPending,
    edit(change: Partial<DraftForm>) {
      if (server === null) return;
      setLocal((current) => ({ ...(current ?? server), ...change }));
    },
    editInputs(change: Partial<PixarSingingAdInputsOutput>) {
      if (server === null) return;
      setLocal((current) => {
        const base = current ?? server;
        return { ...base, inputs: { ...base.inputs, ...change } };
      });
    },
    generateLyrics() {
      if (form === null) return;
      const body = PixarSingingAdLyricsAsk.safeParse({ inputs: form.inputs });
      if (!body.success) return;
      ask();
      lyrics.mutate({ teamId, brandId, data: body.data });
    },
    approveLyrics() {
      if (form === null) return;
      const body = PixarSingingAdLyricsApproval.safeParse({ inputs: form.inputs, lyrics: form.lyrics });
      if (!body.success) return;
      ask();
      lyricsApproval.mutate({ teamId, brandId, data: body.data });
    },
    addTake() {
      ask();
      take.mutate({ teamId, brandId });
    },
    backToLyrics() {
      ask();
      toLyrics.mutate({ teamId, brandId });
    },
    approveSong(number: number) {
      ask();
      songApproval.mutate({ teamId, brandId, data: { take: number } });
    },
    backToTakes() {
      ask();
      toTakes.mutate({ teamId, brandId });
    },
    regenerate(name: PixarSingingStillName) {
      ask();
      regeneration.mutate({ teamId, brandId, name });
    },
    startOver() {
      ask();
      setAsked(null);
      reset.mutate({ teamId, brandId });
    },
  };
}
